import React, { createContext, useState, useContext, useRef, useEffect, useCallback, useMemo } from 'react';

// Create the main audio context
const AudioContext = createContext();

// Create a separate context for time tracking to avoid re-renders
const TimeTrackingContext = createContext();

// Create a provider component
export const AudioProvider = ({ children }) => {
  console.log('[AudioProvider] Component rendering or re-rendering.');

  const [audioSrc, setAudioSrc] = useState(null);
  const [audioTitle, setAudioTitle] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  
  // This ref will store the audio element instance that AudioProvider is *currently* managing listeners for.
  const managedAudioElementRef = useRef(null);
  // This ref will store the cleanup function for the listeners on managedAudioElementRef.current.
  const currentElementCleanupFuncRef = useRef(null);

  const timeUpdateIntervalRef = useRef(null); // For time tracking, independent of managed element.

  const startTimeTracking = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
    }
    timeUpdateIntervalRef.current = setInterval(() => {
      // Use managedAudioElementRef for time updates if it's the source of truth for playback.
      if (managedAudioElementRef.current && !managedAudioElementRef.current.paused) {
        setCurrentTime(managedAudioElementRef.current.currentTime);
      }
    }, 100);
  }, []);

  const stopTimeTracking = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  }, []);

  const playAudio = useCallback((src, title, projectId = null) => {
    console.log('[AudioProvider] playAudio called with src:', src, 'projectId:', projectId);
    setAudioSrc(src); // This will trigger MusicPlayer to update its audioRef.current
    setAudioTitle(title);
    setCurrentProjectId(projectId ? String(projectId) : null); // Ensure string conversion
    // Reset states for new audio
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setShouldAutoPlay(true); // Flag to trigger auto-play
  }, []);

  const closePlayer = useCallback(() => {
    console.log('[AudioProvider] closePlayer called.');
    stopTimeTracking();
    // If setAudioElement(null) is called by MusicPlayer on close, that will also clean up listeners.
    // But ensure states are reset here too.
    if (managedAudioElementRef.current && managedAudioElementRef.current.pause) {
        managedAudioElementRef.current.pause(); // Ensure audio stops
    }
    setAudioSrc(null);
    setAudioTitle('');
    setCurrentProjectId(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setShouldAutoPlay(false);
  }, [stopTimeTracking]);

  const setAudioElement = useCallback((element) => {
    console.log(`[AudioProvider setAudioElement] Called. New element: ${element ? 'VALID' : 'NULL'}. Previously managed: ${managedAudioElementRef.current ? 'VALID' : 'NULL'}`);

    // 1. If there's a cleanup function for a previously managed element, execute it.
    if (typeof currentElementCleanupFuncRef.current === 'function') {
      console.log('[AudioProvider setAudioElement] Executing cleanup for previously managed element:', managedAudioElementRef.current);
      currentElementCleanupFuncRef.current();
    }
    currentElementCleanupFuncRef.current = null;
    managedAudioElementRef.current = null;

    // 2. If a new, valid HTMLAudioElement is provided, set it up.
    if (element && element instanceof HTMLAudioElement) {
      managedAudioElementRef.current = element;
      console.log('[AudioProvider setAudioElement] Now managing new element:', managedAudioElementRef.current);

      const handlePlay = () => {
        if (!managedAudioElementRef.current) return; // Guard
        console.log('[AudioProvider] "play" event DETECTED on element:', managedAudioElementRef.current, 'Setting isPlaying to true. Src:', managedAudioElementRef.current.src);
        setIsPlaying(true);
        startTimeTracking();
      };
      
      const handlePause = () => {
        if (!managedAudioElementRef.current) return; // Guard
        console.log('[AudioProvider] "pause" event DETECTED on element:', managedAudioElementRef.current, 'Setting isPlaying to false. Src:', managedAudioElementRef.current.src);
        setIsPlaying(false);
        stopTimeTracking();
      };
      
      const handleEnded = () => {
        if (!managedAudioElementRef.current) return; // Guard
        console.log('[AudioProvider] "ended" event DETECTED on element:', managedAudioElementRef.current, 'Setting isPlaying to false.');
        setIsPlaying(false);
        stopTimeTracking();
      };

      const handleLoadedMetadata = () => {
        if (!managedAudioElementRef.current) return; // Guard
        const audioDuration = managedAudioElementRef.current.duration;
        console.log('[AudioProvider] "loadedmetadata" event DETECTED. Duration:', audioDuration);
        if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration)) {
          setDuration(audioDuration);
        } else {
          console.warn('[AudioProvider] Invalid duration detected:', audioDuration);
          setDuration(0);
        }
      };

      const handleLoadedData = () => {
        if (!managedAudioElementRef.current) return; // Guard
        const audioDuration = managedAudioElementRef.current.duration;
        console.log('[AudioProvider] "loadeddata" event DETECTED. Duration:', audioDuration);
        if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration)) {
          setDuration(audioDuration);
        }
      };

      const handleCanPlay = () => {
        if (!managedAudioElementRef.current) return; // Guard
        const audioDuration = managedAudioElementRef.current.duration;
        console.log('[AudioProvider] "canplay" event DETECTED. Duration:', audioDuration);
        if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration)) {
          setDuration(audioDuration);
        }
      };
      
      const handleTimeUpdate = () => {
        if (managedAudioElementRef.current) { // Check if still managing this element
          setCurrentTime(managedAudioElementRef.current.currentTime);
        }
      };

      const handleDurationChange = () => {
        if (!managedAudioElementRef.current) return; // Guard
        const audioDuration = managedAudioElementRef.current.duration;
        console.log('[AudioProvider] "durationchange" event DETECTED. Duration:', audioDuration);
        if (audioDuration && !isNaN(audioDuration) && isFinite(audioDuration)) {
          setDuration(audioDuration);
        }
      };
      
      console.log('[AudioProvider setAudioElement] Adding event listeners to element:', managedAudioElementRef.current);
      managedAudioElementRef.current.addEventListener('play', handlePlay);
      managedAudioElementRef.current.addEventListener('pause', handlePause);
      managedAudioElementRef.current.addEventListener('ended', handleEnded);
      managedAudioElementRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      managedAudioElementRef.current.addEventListener('loadeddata', handleLoadedData);
      managedAudioElementRef.current.addEventListener('canplay', handleCanPlay);
      managedAudioElementRef.current.addEventListener('timeupdate', handleTimeUpdate);
      managedAudioElementRef.current.addEventListener('durationchange', handleDurationChange);
      
      // Store the specific cleanup function for *this* element and *these* listeners.
      currentElementCleanupFuncRef.current = () => {
        console.log('[AudioProvider setAudioElement Cleanup] Removing listeners from element:', element);
        // Check element still exists before removing listeners
        if (element) { 
            element.removeEventListener('play', handlePlay);
            element.removeEventListener('pause', handlePause);
            element.removeEventListener('ended', handleEnded);
            element.removeEventListener('loadedmetadata', handleLoadedMetadata);
            element.removeEventListener('loadeddata', handleLoadedData);
            element.removeEventListener('canplay', handleCanPlay);
            element.removeEventListener('timeupdate', handleTimeUpdate);
            element.removeEventListener('durationchange', handleDurationChange);
            console.log('[AudioProvider setAudioElement Cleanup] Listeners successfully removed.');
        }
      };
      
      // If the element already has duration available, set it immediately
      if (element.duration && !isNaN(element.duration) && isFinite(element.duration)) {
        console.log('[AudioProvider setAudioElement] Element already has duration:', element.duration);
        setDuration(element.duration);
      }
      
      return currentElementCleanupFuncRef.current; // Return this cleanup
    } else {
      console.log('[AudioProvider setAudioElement] Received null or invalid element. No new listeners attached.');
      return () => {}; // Return a no-op cleanup
    }
  }, [startTimeTracking, stopTimeTracking]);

  useEffect(() => {
    return () => {
      console.log('[AudioProvider] AudioProvider UNMOUNTING. Cleaning up time tracking and any managed element listeners.');
      stopTimeTracking();
      if (typeof currentElementCleanupFuncRef.current === 'function') {
        currentElementCleanupFuncRef.current(); // Cleanup listeners on unmount
      }
    };
  }, [stopTimeTracking]); // stopTimeTracking is stable

  // Memoize the main context value - exclude currentTime to reduce re-renders
  const mainContextValue = useMemo(() => ({
    audioSrc,
    audioTitle,
    currentProjectId,
    duration,
    isPlaying,
    shouldAutoPlay,
    setShouldAutoPlay,
    playAudio,
    closePlayer,
    setAudioElement
  }), [
    audioSrc, 
    audioTitle, 
    currentProjectId, 
    duration, 
    isPlaying, 
    shouldAutoPlay,
    playAudio, 
    closePlayer, 
    setAudioElement
  ]);

  // Separate context value for time tracking
  const timeContextValue = useMemo(() => ({
    currentTime
  }), [currentTime]);

  return (
    <AudioContext.Provider value={mainContextValue}>
      <TimeTrackingContext.Provider value={timeContextValue}>
        {children}
      </TimeTrackingContext.Provider>
    </AudioContext.Provider>
  );
};

// Hook for components that need all audio functionality except time tracking
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

// Hook for components that only need playAudio functionality (minimal re-renders)
export const useAudioPlayer = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioProvider');
  }
  return {
    playAudio: context.playAudio,
    closePlayer: context.closePlayer
  };
};

// Hook for components that need time tracking (like MusicPlayer)
export const useAudioWithTime = () => {
  const audioContext = useContext(AudioContext);
  const timeContext = useContext(TimeTrackingContext);
  
  if (!audioContext || !timeContext) {
    throw new Error('useAudioWithTime must be used within an AudioProvider');
  }
  
  return {
    ...audioContext,
    currentTime: timeContext.currentTime
  };
}; 